import  { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ToggleButton from "@mui/material/ToggleButton";
import { DateTimePicker, DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import { useAtom } from "jotai";
import { themeAtom } from "../../../../-Atoms";
import { db, doc, updateDoc } from "../../../../firebaseConfig";

const DatesContainer = ({ jobData, isSpecialEvent }) => {
  const [theme] = useAtom(themeAtom);
  const [bookedStart, setBookedStart] = useState(null);
  const [bookedFinish, setBookedFinish] = useState(null);
  const [additionalDates, setAdditionalDates] = useState([]);
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);
  const [allDay, setAllDay] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const handleBookedStartChange = (newValue) => {
    setBookedStart(newValue);

    if (bookedFinish && newValue) {
      const currentEndTime = bookedFinish.format("HH:mm:ss");
      const newEndDate = newValue.format("YYYY-MM-DD") + " " + currentEndTime;
      const updatedEndDate = dayjs(newEndDate);
      setBookedFinish(updatedEndDate);

      updateDoc(doc(db, "JobData", jobData.id), {
        bookedStart: newValue.toISOString(),
        bookedFinish: updatedEndDate.toISOString(),
        start: newValue.toISOString(),
        end: updatedEndDate.toISOString(),
      });
    } else {
      const isoDate = newValue ? newValue.toISOString() : null;
      updateDoc(doc(db, "JobData", jobData.id), {
        bookedStart: isoDate,
        start: isoDate
      });
    }
  };

  const handleAllDayDateChange = (newValue) => {
    setBookedStart(newValue);
    // Set bookedEnd to be a day later when allDay is true
    const nextDay = newValue ? newValue.add(1, 'day') : null;
    setBookedFinish(nextDay);

    const startIso = newValue ? newValue.toISOString() : null;
    const endIso = nextDay ? nextDay.toISOString() : null;

    updateDoc(doc(db, "JobData", jobData.id), {
      bookedStart: startIso,
      bookedFinish: endIso,
      start: startIso,
      end: endIso,
    });
  };

  const handleBookedFinishChange = (newValue) => {
    setBookedFinish(newValue);
    const isoDate = newValue ? newValue.toISOString() : null;
    updateDoc(doc(db, "JobData", jobData.id), {
      bookedFinish: isoDate,
      end: isoDate
    });
  };

  useEffect(() => {
    if (jobData.bookedStart) {
      setBookedStart(dayjs(jobData.bookedStart));
    }
    if (jobData.bookedFinish) {
      setBookedFinish(dayjs(jobData.bookedFinish));
    }
    if (jobData.additionalDates) {
      setAdditionalDates(jobData.additionalDates);
    }
    // Initialize allDay state for special events
    if (isSpecialEvent && jobData.allDay !== undefined) {
      setAllDay(jobData.allDay);
    }
  }, [jobData, isSpecialEvent]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
    hover: { scale: 1.02 },
    tap: { scale: 0.98 },
  };

  const filterButtonStyles = {
    fontWeight: "bold",
    padding: "8px 12px",
    minWidth: { xs: "auto", sm: "70px" },
    border: `1px solid ${
      theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
    } !important`,
    borderRadius: "8px !important",
    fontSize: { xs: 12, sm: 13 },
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    textTransform: "none",
    boxShadow:
      theme === "dark"
        ? "0 2px 5px rgba(0,0,0,0.2)"
        : "0 2px 5px rgba(0,0,0,0.05)",
    whiteSpace: "nowrap",
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormControl
        fullWidth
        sx={{
          marginTop: 2,
          position: "relative",
          "& .MuiInputLabel-root": {
            color:
              theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
            fontWeight: 500,
            fontSize: "0.95rem",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          },
        }}
        variant="filled"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
            borderRadius: "10px",
            overflow: "hidden",
            backgroundColor:
              theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow:
              theme === "dark"
                ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
                : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
            padding: 2,
          }}
        >
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {isSpecialEvent ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <motion.div 
                  variants={itemVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <ToggleButton
                      value="allDay"
                      selected={allDay}
                      size="small"
                      sx={filterButtonStyles}
                      component={motion.button}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        const newAllDay = !allDay;
                        setAllDay(newAllDay);
                        updateDoc(doc(db, "JobData", jobData.id), { allDay: newAllDay });
                      }}
                    >
                      All Day
                    </ToggleButton>
                  </Box>
                </motion.div>

                {allDay ? (
                  <motion.div
                    variants={itemVariants}
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <DatePicker
                      label="Date"
                      value={bookedStart || null}
                      format="DD/MM/YYYY"
                      onChange={handleAllDayDateChange}
                      open={datePickerOpen}
                      onClose={() => setDatePickerOpen(false)}
                      slots={{
                        openPickerIcon: () => null,
                      }}
                      slotProps={{
                        textField: {
                          variant: "filled",
                          fullWidth: true,
                          onClick: () => setDatePickerOpen(true),
                          InputProps: {
                            disableUnderline: true,
                            readOnly: true,
                            sx: {
                              cursor: "pointer",
                              borderRadius: "8px",
                              backgroundColor:
                                theme === "dark"
                                  ? "rgba(45,45,45,0.7)"
                                  : "rgba(250,250,250,0.9)",
                              boxShadow:
                                theme === "dark"
                                  ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                                  : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                              "&:hover": {
                                backgroundColor:
                                  theme === "dark"
                                    ? "rgba(55,55,55,0.8)"
                                    : "rgba(255,255,255,1)",
                                boxShadow:
                                  theme === "dark"
                                    ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                                    : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
                              },
                            },
                          },
                        },
                      }}
                    />
                  </motion.div>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 2,
                      flexWrap: "nowrap",
                    }}
                  >
                    <motion.div
                      variants={itemVariants}
                      style={{ flex: 1, minWidth: 0 }}
                    >
                      <DateTimePicker
                        label="Start Date"
                        value={bookedStart || null}
                        format="DD/MM/YYYY hh:mm A"
                        onChange={handleBookedStartChange}
                        open={startPickerOpen}
                        onClose={() => setStartPickerOpen(false)}
                        slots={{
                          openPickerIcon: () => null,
                        }}
                        slotProps={{
                          textField: {
                            variant: "filled",
                            fullWidth: true,
                            onClick: () => setStartPickerOpen(true),
                            InputProps: {
                              disableUnderline: true,
                              readOnly: true,
                              sx: {
                                cursor: "pointer",
                                borderRadius: "8px",
                                backgroundColor:
                                  theme === "dark"
                                    ? "rgba(45,45,45,0.7)"
                                    : "rgba(250,250,250,0.9)",
                                boxShadow:
                                  theme === "dark"
                                    ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                                    : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                                "&:hover": {
                                  backgroundColor:
                                    theme === "dark"
                                      ? "rgba(55,55,55,0.8)"
                                      : "rgba(255,255,255,1)",
                                  boxShadow:
                                    theme === "dark"
                                      ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                                      : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
                                },
                              },
                            },
                          },
                        }}
                      />
                    </motion.div>

                    <motion.div
                      variants={itemVariants}
                      style={{ flex: 1, minWidth: 0 }}
                    >
                      <DateTimePicker
                        label="End Date"
                        value={bookedFinish || null}
                        format="DD/MM/YYYY hh:mm A"
                        onChange={handleBookedFinishChange}
                        open={endPickerOpen}
                        onClose={() => setEndPickerOpen(false)}
                        slots={{
                          openPickerIcon: () => null,
                        }}
                        slotProps={{
                          textField: {
                            variant: "filled",
                            fullWidth: true,
                            onClick: () => setEndPickerOpen(true),
                            InputProps: {
                              disableUnderline: true,
                              readOnly: true,
                              sx: {
                                cursor: "pointer",
                                borderRadius: "8px",
                                backgroundColor:
                                  theme === "dark"
                                    ? "rgba(45,45,45,0.7)"
                                    : "rgba(250,250,250,0.9)",
                                boxShadow:
                                  theme === "dark"
                                    ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                                    : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                                "&:hover": {
                                  backgroundColor:
                                    theme === "dark"
                                      ? "rgba(55,55,55,0.8)"
                                      : "rgba(255,255,255,1)",
                                  boxShadow:
                                    theme === "dark"
                                      ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                                      : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
                                },
                              },
                            },
                          },
                        }}
                      />
                    </motion.div>
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                  mb: additionalDates.length > 0 ? 0 : -2,
                  flexWrap: "nowrap",
                }}
              >
                <motion.div
                  variants={itemVariants}
                  style={{ flex: 1, minWidth: 0 }}
                >
                  <DateTimePicker
                    label="Start Date"
                    value={bookedStart || null}
                    format="DD/MM/YYYY hh:mm A"
                    onChange={handleBookedStartChange}
                    open={startPickerOpen}
                    onClose={() => setStartPickerOpen(false)}
                    slots={{
                      openPickerIcon: () => null,
                    }}
                    slotProps={{
                      textField: {
                        variant: "filled",
                        fullWidth: true,
                        onClick: () => setStartPickerOpen(true),
                        InputProps: {
                          disableUnderline: true,
                          readOnly: true,
                          sx: {
                            cursor: "pointer",
                            borderRadius: "8px",
                            backgroundColor:
                              theme === "dark"
                                ? "rgba(45,45,45,0.7)"
                                : "rgba(250,250,250,0.9)",
                            boxShadow:
                              theme === "dark"
                                ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                                : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                            "&:hover": {
                              backgroundColor:
                                theme === "dark"
                                  ? "rgba(55,55,55,0.8)"
                                  : "rgba(255,255,255,1)",
                              boxShadow:
                                theme === "dark"
                                  ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                                  : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
                            },
                          },
                        },
                      },
                    }}
                  />
                </motion.div>

                <motion.div
                  variants={itemVariants}
                  style={{ flex: 1, minWidth: 0 }}
                >
                  <DateTimePicker
                    label="End Date"
                    value={bookedFinish || null}
                    format="DD/MM/YYYY hh:mm A"
                    onChange={handleBookedFinishChange}
                    open={endPickerOpen}
                    onClose={() => setEndPickerOpen(false)}
                    slots={{
                      openPickerIcon: () => null,
                    }}
                    slotProps={{
                      textField: {
                        variant: "filled",
                        fullWidth: true,
                        onClick: () => setEndPickerOpen(true),
                        InputProps: {
                          disableUnderline: true,
                          readOnly: true,
                          sx: {
                            cursor: "pointer",
                            borderRadius: "8px",
                            backgroundColor:
                              theme === "dark"
                                ? "rgba(45,45,45,0.7)"
                                : "rgba(250,250,250,0.9)",
                            boxShadow:
                              theme === "dark"
                                ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                                : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                            "&:hover": {
                              backgroundColor:
                                theme === "dark"
                                  ? "rgba(55,55,55,0.8)"
                                  : "rgba(255,255,255,1)",
                              boxShadow:
                                theme === "dark"
                                  ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                                  : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
                            },
                          },
                        },
                      },
                    }}
                  />
                </motion.div>

                <motion.div
                  variants={itemVariants}
                  whileHover="hover"
                  whileTap="tap"
                  style={{ flexShrink: 0 }}
                >
                  <IconButton
                    onClick={() => {
                      additionalDates.push({
                        start: null,
                        end: null,
                        photographer: "",
                        additionalPhotographers: [],
                        operator: "",
                        additionalOperators: [],
                        stylist: "",
                        additionalStylists: [],
                      });
                      setAdditionalDates([...additionalDates]);
                      updateDoc(doc(db, "JobData", jobData.id), { additionalDates });
                    }}
                    sx={{
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(60,60,60,0.8)"
                          : "rgba(235,235,235,0.9)",
                      boxShadow:
                        theme === "dark"
                          ? "0 2px 5px rgba(0,0,0,0.2)"
                          : "0 2px 5px rgba(0,0,0,0.05)",
                      "&:hover": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(80,80,80,0.9)"
                            : "rgba(220,220,220,0.9)",
                      },
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </motion.div>
              </Box>
            )}
          </motion.div>

          <AdditionalDates
                        jobData={jobData}
            additionalDates={additionalDates}
            setAdditionalDates={setAdditionalDates}
            theme={theme}
          />
        </Box>
      </FormControl>
    </LocalizationProvider>
  );
};

export default DatesContainer;

const AdditionalDates = ({
  jobData,
  additionalDates,
  setAdditionalDates,
  theme,
}) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      {additionalDates &&
        additionalDates.map((additionalDate, index) => (
          <AdditionalDate
            key={`${additionalDate.start}-${index}`}
            index={index}
            start={additionalDate.start}
            end={additionalDate.end}
            additionalDates={additionalDates}
            setAdditionalDates={setAdditionalDates}
            jobData={jobData}
                        theme={theme}
          />
        ))}
    </motion.div>
  );
};

const AdditionalDate = ({
  index,
  start,
  end,
  additionalDates,
  setAdditionalDates,
  jobData,
  theme,
}) => {
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);

  const startValue = start ? dayjs(start) : null;
  const endValue = end ? dayjs(end) : null;

  const handleStartChange = (newValue) => {
    try {
      additionalDates[index].start = newValue.toISOString();

      // If there's an end date, update it to the same date but keep the time
      if (additionalDates[index].end && newValue) {
        const currentEndDate = dayjs(additionalDates[index].end);
        const currentEndTime = currentEndDate.format("HH:mm:ss");
        const newEndDate = newValue.format("YYYY-MM-DD") + " " + currentEndTime;
        const updatedEndDate = dayjs(newEndDate);
        additionalDates[index].end = updatedEndDate.toISOString();
      }

      updateAdditionalDates(
        additionalDates,
        setAdditionalDates,
        jobData
      );
    } catch (error) {
      console.log(error);
    }
  };

  const handleEndChange = (newValue) => {
    try {
      additionalDates[index].end = newValue.toISOString();
      updateAdditionalDates(
        additionalDates,
        setAdditionalDates,
        jobData
      );
    } catch (error) {
      console.log(error);
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
    hover: { scale: 1.02 },
    tap: { scale: 0.98 },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <motion.div
        variants={itemVariants}
        initial="hidden"
        animate="visible"
        exit={{ opacity: 0, y: -10 }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            borderRadius: "8px",
            mb: index < additionalDates.length - 1 ? 2 : 0,
            flexWrap: "nowrap",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DateTimePicker
              label="Additional Start Date"
              value={startValue}
              format="DD/MM/YYYY hh:mm A"
              onChange={handleStartChange}
              open={startPickerOpen}
              onClose={() => setStartPickerOpen(false)}
              slots={{
                openPickerIcon: () => null,
              }}
              slotProps={{
                textField: {
                  variant: "filled",
                  fullWidth: true,
                  onClick: () => setStartPickerOpen(true),
                  InputProps: {
                    disableUnderline: true,
                    readOnly: true,
                    sx: {
                      cursor: "pointer",
                      borderRadius: "8px",
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(45,45,45,0.7)"
                          : "rgba(250,250,250,0.9)",
                      boxShadow:
                        theme === "dark"
                          ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                          : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                      "&:hover": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(55,55,55,0.8)"
                            : "rgba(255,255,255,1)",
                        boxShadow:
                          theme === "dark"
                            ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                            : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
                      },
                    },
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DateTimePicker
              label="Additional End Date"
              value={endValue}
              format="DD/MM/YYYY hh:mm A"
              onChange={handleEndChange}
              open={endPickerOpen}
              onClose={() => setEndPickerOpen(false)}
              slots={{
                openPickerIcon: () => null,
              }}
              slotProps={{
                textField: {
                  variant: "filled",
                  fullWidth: true,
                  onClick: () => setEndPickerOpen(true),
                  InputProps: {
                    disableUnderline: true,
                    readOnly: true,
                    sx: {
                      cursor: "pointer",
                      borderRadius: "8px",
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(45,45,45,0.7)"
                          : "rgba(250,250,250,0.9)",
                      boxShadow:
                        theme === "dark"
                          ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                          : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                      "&:hover": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(55,55,55,0.8)"
                            : "rgba(255,255,255,1)",
                        boxShadow:
                          theme === "dark"
                            ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                            : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
                      },
                    },
                  },
                },
              }}
            />
          </Box>

          <motion.div
            whileHover="hover"
            whileTap="tap"
            variants={itemVariants}
            style={{ flexShrink: 0 }}
          >
            <IconButton
              onClick={() => {
                additionalDates.splice(index, 1);
                setAdditionalDates([...additionalDates]);
                updateDoc(doc(db, "JobData", jobData.id), { additionalDates });
              }}
              sx={{
                backgroundColor:
                  theme === "dark"
                    ? "rgba(60,60,60,0.8)"
                    : "rgba(235,235,235,0.9)",
                boxShadow:
                  theme === "dark"
                    ? "0 2px 5px rgba(0,0,0,0.2)"
                    : "0 2px 5px rgba(0,0,0,0.05)",
                "&:hover": {
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(80,80,80,0.9)"
                      : "rgba(220,220,220,0.9)",
                },
              }}
            >
              <RemoveIcon />
            </IconButton>
          </motion.div>
        </Box>
      </motion.div>
    </LocalizationProvider>
  );
};

const updateAdditionalDates = (
  additionalDates,
  setAdditionalDates,
  jobData
) => {
  setAdditionalDates([...additionalDates]);
  updateDoc(doc(db, "JobData", jobData.id), { additionalDates });
};
